/**
 * ★ O PROTOCOLO do canal de voz em tempo real, como ele foi MEDIDO —
 * [spec-008 T030]. Nomes de evento, motivos de falha, os dois prazos
 * derivados de medição e as duas primitivas sem estado (suporte a WebRTC e
 * espera da coleta de ICE).
 *
 * Irmão de `transporte-tempo-real.js`, que é quem tem o estado da sessão.
 * Separado dele por RESPONSABILIDADE, não só por tamanho: aqui está o que o
 * PROVEDOR faz — fatos que valem independentemente do nosso desenho e que
 * mudam por medição nova, nunca por refactor; lá está o que NÓS fazemos com
 * isso. A separação também é o que permite testar os fatos sem montar sessão
 * nenhuma.
 *
 * ## De onde cada fato saiu
 *
 * Sessões WebRTC reais dos portões `verdicts/g11-client-delegation-real.md`,
 * `g12-modo-responses.md` e `g13-responses-conversa-na-espera.md` — 7 sessões,
 * 812 eventos servidor→cliente nos JSONs crus. **Nada aqui vem de doc genérica
 * do provedor**; a v1 desta spec já havia errado assim uma vez (a "credencial
 * efêmera" da D-1, corrigida por medição).
 *
 * | fato medido | onde aparece |
 * |---|---|
 * | o data channel se chama `oai-events` | `NOME_DO_CANAL_DE_EVENTOS` |
 * | servidor→cliente existem `session.started`, `session.input_transcript.delta`, `session.output_transcript.delta`, `session.delegation.created`, `session.usage.updated`, `response.event`, `session.closed`, `error` | os `EVENTO_*` abaixo — e **nada além** |
 * | **NÃO existe** `*.done`/`*.completed` de transcrição, nem `speech_started`/`speech_stopped`, nem evento de "parei de falar" | por isso `MS_SILENCIO_ATE_FIM_DA_FALA_DA_IA` existe |
 * | o consumo cobrável é nativo: `session.usage.updated → {usage:{seconds}}`, repetido no `session.closed` | `EVENTO_CONSUMO` |
 * | cliente→servidor existem só `session.start`, `session.update`, `session.input_audio.{append,mute,unmute}`, `session.{instructions,thinking,commentary}.append`, `response.item.create`, `response.create`, `session.close` | `EVENTO_FECHAR_SESSAO` — e a ausência de um "cancelar fala", que decide o `interromper` do transporte |
 * | fechar pedido por nós volta com `reason: 'close_requested'` | `MOTIVO_FECHAMENTO_PEDIDO` |
 * | **[T032]** no modo `responses` a demanda chega TIPADA, dentro de `response.event` → `event.output_item.done` → `item.{name,arguments,call_id}`, e `arguments` é **string JSON** | `EVENTO_DA_RESPONSES`, `EVENTO_ITEM_DA_RESPONSES_CONCLUIDO` |
 * | **[T032]** o laço fecha por `call_id` (`response.item.create` + `response.create`); `delegation_id` nos dois volta `Unknown parameter` | `EVENTO_CRIAR_ITEM`, `EVENTO_CRIAR_RESPOSTA`, `MOTIVO_ID_DE_DELEGACAO_NO_FIO` |
 * | **[T032]** os `*.append` só passam com `delegation_id: null`; com o id real, `Unknown client delegation.` em 6/6 | `EVENTO_APPEND_DE_*`, `MOTIVO_ID_DE_DELEGACAO_NO_FIO` |
 * | **[T032]** o modo vive na CRIAÇÃO e é ecoado em `session.started.session.delegation.type`; `session.delegation.created.delegation.target` o repete | `MODO_DELEGACAO_RESPONSES`, `MOTIVO_MODO_DE_DELEGACAO` |
 */

/** O nome do data channel, observado em 7 sessões reais (G-11 §0). */
export const NOME_DO_CANAL_DE_EVENTOS = 'oai-events'

// Eventos servidor→cliente que o transporte consome. Nomeados — nunca
// literais espalhados pelos handlers, e nunca inventados.
export const EVENTO_TRANSCRICAO_DO_USUARIO = 'session.input_transcript.delta'
export const EVENTO_TRANSCRICAO_DA_IA = 'session.output_transcript.delta'
export const EVENTO_CONSUMO = 'session.usage.updated'
export const EVENTO_SESSAO_FECHADA = 'session.closed'
export const EVENTO_ERRO = 'error'

/** Cliente→servidor. Destes, só o fechamento é usado pelo transporte. */
export const EVENTO_FECHAR_SESSAO = 'session.close'

// ── [T032] A DELEGAÇÃO no modo `responses` ───────────────────────────────
// Tudo abaixo saiu dos JSONs crus do G-12/G-13 (`~/aawz-tmp/spec008-g1{2,3}/`),
// lido evento a evento — não da tabela dos verdicts, que resume. Onde o
// verdict e o fio discordam, vale o fio, e a diferença está dita.

/**
 * A abertura ECOA a configuração inteira da sessão, `delegation` inclusa
 * (`evento.session.delegation.type`) — medido em `sessaoL.eventos.json`. É
 * por este eco que o transporte CONFERE o modo: ele não pode escolhê-lo.
 */
export const EVENTO_SESSAO_INICIADA = 'session.started'

/**
 * O gatilho da delegação. Não traz o texto da tarefa; traz `delegation.target`
 * (`'responses'` | `'client'`), que é a segunda testemunha do modo — presente
 * em 6/6 relatórios do G-13, nos dois modos.
 */
export const EVENTO_DELEGACAO_CRIADA = 'session.delegation.created'

/**
 * O envelope da Responses API: `{delegation_id, event:{...}}`, onde `event` é
 * um evento de streaming LITERAL da Responses (`response.created`,
 * `...arguments.delta`, `response.output_item.done`, …). Só existe no modo
 * `responses` — 21 deles na sessão L.
 */
export const EVENTO_DA_RESPONSES = 'response.event'

/**
 * O evento INTERNO que fecha o item: `event.item` traz `type`, `name`,
 * `arguments` e `call_id`. ★ `arguments` é **string JSON**, não objeto:
 * `"{\"cliente\":\"Teste Alpha\",\"data\":\"2026-08-31\"}"`.
 * O `...output_item.added` chega antes com `arguments: ''` — é por isso que a
 * demanda é lida no `.done` e nunca no `.added`.
 */
export const EVENTO_ITEM_DA_RESPONSES_CONCLUIDO = 'response.output_item.done'

export const TIPO_ITEM_CHAMADA_DE_FUNCAO = 'function_call'
export const TIPO_ITEM_RESULTADO_DE_FUNCAO = 'function_call_output'

/** O modo de delegação que esta spec adotou (D-4, G-13 §7). */
export const MODO_DELEGACAO_RESPONSES = 'responses'

// Cliente→servidor do laço. As formas são as EXERCITADAS no G-12 (sessões I e
// K) e no G-13 (sondagem R) — nenhuma inventada:
//
//   {"type":"response.item.create",
//    "item":{"type":"function_call_output","call_id":"call_…","output":"<string>"}}
//   {"type":"response.create"}                            ← vazio, sem `delegation_id`
//   {"type":"session.instructions.append","delegation_id":null,"content":"…"}
//
// ⚠️ `output` é **string**, e `delegation_id` NÃO existe em nenhuma das duas
// primeiras (`Unknown parameter: 'delegation_id'.`, G-12 §5).
export const EVENTO_CRIAR_ITEM = 'response.item.create'
export const EVENTO_CRIAR_RESPOSTA = 'response.create'
export const EVENTO_APPEND_DE_INSTRUCOES = 'session.instructions.append'
export const EVENTO_APPEND_DE_COMENTARIO = 'session.commentary.append'
export const EVENTO_APPEND_DE_RACIOCINIO = 'session.thinking.append'

/** O `reason` que o provedor devolve quando FOMOS nós que pedimos o fim. */
export const MOTIVO_FECHAMENTO_PEDIDO = 'close_requested'

// Motivos NOMEADOS — o contrato (`contracts/transporte.md`) pede que `abrir`
// rejeite com erro nomeado e que `erro`/`queda` levem `motivo`. Nenhum carrega
// valor de segredo (N7): são rótulos nossos, e o detalhe técnico vai à parte.
export const MOTIVO_SEM_SUPORTE = 'sem_suporte_webrtc'
export const MOTIVO_SEM_SUPERFICIE = 'superficie_nao_declarada'
export const MOTIVO_MICROFONE = 'microfone_indisponivel'
export const MOTIVO_SESSAO_RECUSADA = 'sessao_recusada'
export const MOTIVO_CANAL_CAIU = 'canal_caiu'
export const MOTIVO_ERRO_DO_PROVEDOR = 'erro_do_provedor'

// ── [T032] Motivos da delegação — cada um é um invariante medido ─────────

/**
 * ★ N-modo — a sessão não nasceu em `responses`. O modo é escolhido na
 * CRIAÇÃO e **não muda depois** (`The delegation type cannot change after
 * session startup.`, G-12 §2), então o navegador não tem como corrigir: ele
 * confere e cai. Uma sessão em `client` nunca emite `function_call` — seguir
 * nela seria o consultor esperando por uma consulta que ninguém vai receber.
 */
export const MOTIVO_MODO_DE_DELEGACAO = 'modo_de_delegacao_nao_e_responses'

/**
 * ★ N20 — serial por sessão. Com um `function_call` pendente, o provedor
 * recusa `response.create` (*"Submit the pending function call outputs before
 * response.create."*, G-13 §3). A recusa acontece do NOSSO lado, antes de ir
 * ao fio.
 */
export const MOTIVO_DELEGACAO_EM_CURSO = 'delegacao_em_curso'

/**
 * ★ N19 — alguém tentou pôr um id de delegação no fio. Nos `*.append` isso
 * volta `Unknown client delegation.` (6/6 no G-13 §4) e no `response.item.create`
 * volta `Unknown parameter: 'delegation_id'.` (G-12 §5) — 100% de falha, e o
 * canal fica MUDO (RMS 0,08). Falha alto aqui, antes do envio.
 */
export const MOTIVO_ID_DE_DELEGACAO_NO_FIO = 'id_de_delegacao_no_fio'

/**
 * ★ N21 — chegou demanda e não há turno transcrito. `arguments` **não**
 * substitui a fala: ele desambigua, nunca faz lookup (`Teste Alfa` →
 * `"Teste Alpha"`/`"Teste Z"` em 7/8 medições, G-12 §6 + G-13 §3).
 */
export const MOTIVO_DEMANDA_SEM_TURNO = 'demanda_sem_turno_transcrito'

/**
 * ★ [T040b] N21, a outra metade — a demanda TEM turno, mas o identificador que
 * o roteador do provedor preencheu não corresponde ao que o consultor falou
 * (`Teste Alfa` → `"Teste Alpha"`, 7/8 medições). Sem correspondência exata a
 * voz PERGUNTA; adivinhar acha a entidade errada em silêncio.
 */
export const MOTIVO_IDENTIFICADOR_NAO_CONFERE = 'identificador_nao_confere'

/** A delegação ao `POST /chat` falhou — o detalhe técnico NÃO vai ao canal. */
export const MOTIVO_DELEGACAO_FALHOU = 'delegacao_falhou'

/** Resultado montado sem `call_id` — sem ele o laço não fecha em ninguém. */
export const MOTIVO_RESULTADO_SEM_CALL_ID = 'resultado_sem_call_id'

/**
 * ★ D-13 — a saída que iria ao canal falado carrega dado de negócio. O
 * número, o cliente, o ativo e a data-base vão ao CARTÃO; a voz remete.
 */
export const MOTIVO_SAIDA_RECITA_DADO = 'saida_recita_dado_de_negocio'

/**
 * `origem` de todo turno falado — o valor é do BACKEND
 * (`core/origem_do_turno.py::ORIGEM_VOZ_TEMPO_REAL`), copiado byte a byte.
 * É esta marca que faz `confirmacao_do_servidor` recusar confirmação natural
 * (D-5, US-5 AC1); escrevê-la errado desarma a guarda em silêncio.
 */
export const ORIGEM_VOZ_TEMPO_REAL = 'voz_tempo_real'

/**
 * Teto da espera pela coleta de ICE antes de mandar o offer. O offer viaja
 * numa requisição HTTP e não há canal de sinalização de volta para candidatos
 * avulsos — ou seja, é ICE "vanilla": o offer precisa sair completo.
 *
 * 3 s é TETO, não expectativa: a sessão é criada SEM servidores STUN/TURN (é
 * assim que as 7 sessões medidas conectaram), então só há candidatos `host`,
 * que o navegador reúne em milissegundos. Estourado o teto, manda-se o que já
 * existe em vez de desistir — offer parcial costuma bastar, offer nenhum
 * nunca conecta.
 */
export const MS_ESPERA_DA_COLETA_DE_ICE = 3000

/**
 * Silêncio no fio de transcrição da IA que conta como "ela terminou de falar".
 *
 * ★ **O número é medido, não escolhido.** Nas 7 sessões, entre dois
 * `session.output_transcript.delta` consecutivos: o maior intervalo DENTRO de
 * uma mesma fala foi **2,13 s** (263 amostras) e o menor intervalo ENTRE falas
 * separadas foi **5,02 s** (26 amostras) — a faixa `(2,13 s ; 5,02 s)` ficou
 * VAZIA. 3 s cai dentro dela, então nenhuma das 289 amostras seria
 * classificada errado por este valor.
 */
export const MS_SILENCIO_ATE_FIM_DA_FALA_DA_IA = 3000

/** Erro NOMEADO de abertura — quem chama decide a queda para `porTurnos`. */
export class ErroDeVozTempoReal extends Error {
  constructor(motivo, mensagem) {
    super(mensagem || motivo)
    this.name = 'ErroDeVozTempoReal'
    this.motivo = motivo
  }
}

/**
 * O navegador fala WebRTC? Checagem de GRAÇA e sem prompt — por isso vem antes
 * do pedido de microfone, que abre diálogo de permissão. `MediaRecorder` e
 * `AudioContext` (que `suportaEntradaDeVoz` exige) NÃO entram: o caminho em
 * tempo real não grava Blob nem mede nível, e exigi-los recusaria navegador
 * que atende este transporte perfeitamente.
 */
export const suportaWebRtc = () =>
  typeof globalThis.RTCPeerConnection === 'function'

/**
 * Espera a coleta de candidatos ICE terminar, com teto. **Resolve SEMPRE** — o
 * estouro do teto é caminho normal, não falha (ver `MS_ESPERA_DA_COLETA_DE_ICE`).
 *
 * @param {RTCPeerConnection} pc
 * @param {number} [prazoMs]
 * @returns {Promise<void>}
 */
export const esperarColetaDeIce = (pc, prazoMs = MS_ESPERA_DA_COLETA_DE_ICE) =>
  new Promise((resolve) => {
    if (!pc || pc.iceGatheringState === 'complete') {
      resolve()
      return
    }
    let resolvido = false
    let temporizador = null
    const concluir = () => {
      if (resolvido) return
      resolvido = true
      clearTimeout(temporizador)
      pc.onicegatheringstatechange = null
      resolve()
    }
    temporizador = setTimeout(concluir, prazoMs)
    pc.onicegatheringstatechange = () => {
      if (pc.iceGatheringState === 'complete') concluir()
    }
  })

// ── [T032] As formas do fio da delegação ────────────────────────────────
// Montadores e leitor SEM ESTADO — mesma natureza de `esperarColetaDeIce`:
// são o que o PROVEDOR aceita e emite, e mudam por medição nova, nunca por
// refactor nosso. A POLÍTICA (o que se delega, o que se fala de volta) fica
// em `transporte-tempo-real-delegacao.js`.

/**
 * ★ N19 — a recusa do id de delegação, no único lugar que importa: a montagem
 * do que vai ao fio. Recusa ANTES do envio porque o provedor não recusa alto:
 * ele responde `Unknown client delegation.` / `Unknown parameter:
 * 'delegation_id'.` e o canal fica MUDO (RMS 0,08 em 6/6, G-13 §4).
 */
const _recusarIdDeDelegacao = (idDaDelegacao, onde) => {
  if (idDaDelegacao === undefined || idDaDelegacao === null) return
  throw new ErroDeVozTempoReal(
    MOTIVO_ID_DE_DELEGACAO_NO_FIO,
    `${onde} não aceita id de delegação no modo 'responses' (o laço se amarra ` +
      'pelo `call_id`); com o id, o provedor recusa e o canal emudece.'
  )
}

/**
 * `{"type":"response.item.create","item":{"type":"function_call_output",
 * "call_id":"call_…","output":"<string>"}}` — forma exercitada nas sessões I e
 * K do G-12. `output` é **string**: quem passar objeto manda `[object Object]`.
 */
export const montarResultadoDaFuncao = ({ callId, saida, idDaDelegacao }) => {
  _recusarIdDeDelegacao(idDaDelegacao, 'response.item.create')
  if (!callId) {
    throw new ErroDeVozTempoReal(
      MOTIVO_RESULTADO_SEM_CALL_ID,
      'Resultado sem `call_id`: é ele, e só ele, que amarra o laço.'
    )
  }
  return {
    type: EVENTO_CRIAR_ITEM,
    item: {
      type: TIPO_ITEM_RESULTADO_DE_FUNCAO,
      call_id: String(callId),
      output: JSON.stringify(saida),
    },
  }
}

/** `{"type":"response.create"}` — vazio; com `delegation_id` é recusado. */
export const montarPedidoDeResposta = ({ idDaDelegacao } = {}) => {
  _recusarIdDeDelegacao(idDaDelegacao, 'response.create')
  return { type: EVENTO_CRIAR_RESPOSTA }
}

/**
 * A família `*.append` — a única em que `delegation_id` aparece, e **sempre
 * `null`** (G-13 §4: com o id real, `Unknown client delegation.` 6/6 e
 * silêncio; com `null`, `session.instructions.appended` e o modelo falou).
 *
 * ★ **[T063] Passou a ter chamador**, e a justificativa de não ter mentia.
 * Ela dizia que *"o modelo já conversa sozinho durante a espera (9/9, G-13
 * §1), então empurrar é opção"* — mas as 9/9 do G-13 são **respostas a
 * interpelações** (o método do portão é, literal, *"delegação aberta … e eu
 * falando por cima"*). Com o consultor CALADO, que é o caso real, o modelo
 * anuncia uma vez e fica mudo: **31,1 s sem um único
 * `session.output_transcript.delta`** com a delegação pendente, medidos em
 * navegador real ([T050], `~/aawz-tmp/spec008-t050/eventos-browser.json`).
 *
 * Quem empurra agora é `transporte-tempo-real-acompanhamento.js`, e é por
 * aqui que ele passa — herdando a recusa de N19.
 */
export const montarAppendDeFala = ({ tipo, conteudo, idDaDelegacao }) => {
  _recusarIdDeDelegacao(idDaDelegacao, tipo)
  return { type: tipo, delegation_id: null, content: String(conteudo || '') }
}

const _lerArgumentos = (bruto) => {
  if (!bruto) return {}
  if (typeof bruto === 'object') return bruto
  try {
    const lido = JSON.parse(String(bruto))
    return lido && typeof lido === 'object' ? lido : {}
  } catch (erro) {
    // Argumento ilegível não derruba a demanda: ele é PISTA (N21), e o que
    // vira consulta é o turno transcrito. Sem pista, delega-se do mesmo jeito.
    return {}
  }
}

/**
 * Lê a demanda TIPADA de dentro do envelope `response.event`. `null` para
 * qualquer outro evento da Responses — e são 20 dos 21 numa sessão medida
 * (`response.created`, `…arguments.delta` × 15, `response.completed`…).
 *
 * A leitura é no `…output_item.done` e nunca no `…added`: lá `arguments` ainda
 * é `''`, e delegar com argumento vazio seria consultar o nada.
 */
export const lerDemandaDeFuncao = (evento) => {
  const interno = (evento && evento.event) || {}
  if (interno.type !== EVENTO_ITEM_DA_RESPONSES_CONCLUIDO) return null
  const item = interno.item || {}
  if (item.type !== TIPO_ITEM_CHAMADA_DE_FUNCAO) return null
  if (!item.call_id) return null
  return {
    nome: String(item.name || ''),
    callId: String(item.call_id),
    argumentos: _lerArgumentos(item.arguments),
  }
}
